/**
 * Sound settings — pure data layer (no React).
 *
 * Read/write sound preferences from localStorage.
 * Consumed by lib functions (audioContext, tradeAudio, defenseAudio)
 * and by the useSoundSettings React hook.
 */

const STORAGE_KEY = 'galactus:soundSettings';

export interface SoundSettings {
  tradeSoundEnabled: boolean;
  whaleSoundEnabled: boolean;
  firstSaleSoundEnabled: boolean;
  defenseSoundEnabled: boolean;
  sweepSoundEnabled: boolean;
  exposureBreachSoundEnabled: boolean;
  adverseFlowSoundEnabled: boolean;
  queueFrontSoundEnabled: boolean;
  bestPriceSoundEnabled: boolean;
  orderPlacedSoundEnabled: boolean;
  orderRejectionSoundEnabled: boolean;
  bootSoundEnabled: boolean;
  volume: number; // 0-1
  /** Minimum contracts in a fill to trigger sound (0 = no minimum) */
  minFillContracts: number;
}

export const SOUND_DEFAULTS: SoundSettings = {
  tradeSoundEnabled: true,
  whaleSoundEnabled: true,
  firstSaleSoundEnabled: true,
  defenseSoundEnabled: true,
  sweepSoundEnabled: true,
  exposureBreachSoundEnabled: true,
  adverseFlowSoundEnabled: true,
  queueFrontSoundEnabled: true,
  bestPriceSoundEnabled: true,
  orderPlacedSoundEnabled: true,
  orderRejectionSoundEnabled: true,
  bootSoundEnabled: true,
  volume: 0.5,
  minFillContracts: 0,
};

export function readSoundSettings(): SoundSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...SOUND_DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<SoundSettings>;
    return {
      tradeSoundEnabled: parsed.tradeSoundEnabled ?? SOUND_DEFAULTS.tradeSoundEnabled,
      whaleSoundEnabled: parsed.whaleSoundEnabled ?? SOUND_DEFAULTS.whaleSoundEnabled,
      firstSaleSoundEnabled: parsed.firstSaleSoundEnabled ?? SOUND_DEFAULTS.firstSaleSoundEnabled,
      defenseSoundEnabled: parsed.defenseSoundEnabled ?? SOUND_DEFAULTS.defenseSoundEnabled,
      sweepSoundEnabled: parsed.sweepSoundEnabled ?? SOUND_DEFAULTS.sweepSoundEnabled,
      exposureBreachSoundEnabled:
        parsed.exposureBreachSoundEnabled ?? SOUND_DEFAULTS.exposureBreachSoundEnabled,
      adverseFlowSoundEnabled:
        parsed.adverseFlowSoundEnabled ?? SOUND_DEFAULTS.adverseFlowSoundEnabled,
      queueFrontSoundEnabled:
        parsed.queueFrontSoundEnabled ?? SOUND_DEFAULTS.queueFrontSoundEnabled,
      bestPriceSoundEnabled: parsed.bestPriceSoundEnabled ?? SOUND_DEFAULTS.bestPriceSoundEnabled,
      orderPlacedSoundEnabled:
        parsed.orderPlacedSoundEnabled ?? SOUND_DEFAULTS.orderPlacedSoundEnabled,
      orderRejectionSoundEnabled:
        parsed.orderRejectionSoundEnabled ?? SOUND_DEFAULTS.orderRejectionSoundEnabled,
      bootSoundEnabled: parsed.bootSoundEnabled ?? SOUND_DEFAULTS.bootSoundEnabled,
      volume:
        typeof parsed.volume === 'number'
          ? Math.max(0, Math.min(1, parsed.volume))
          : SOUND_DEFAULTS.volume,
      minFillContracts:
        typeof parsed.minFillContracts === 'number'
          ? Math.max(0, Math.floor(parsed.minFillContracts))
          : SOUND_DEFAULTS.minFillContracts,
    };
  } catch {
    return { ...SOUND_DEFAULTS };
  }
}

export function writeSoundSettings(settings: SoundSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage full or unavailable
  }
}
